import { useState } from "react";
import { Link } from "react-router-dom";

// ─── SHARED COLORS (reference only — applied via className) ───────────────
// White:       #FFFFFF  — main background
// Beige:       #F4F1EA  — alternate sections
// Dark text:   #1A1A1A
// Deep olive:  #2F3E2F  — primary buttons, key accents
// Stone gray:  #B8B2A6  — borders, placeholders

// ─── IMAGE PLACEHOLDER ────────────────────────────────────────────────────
const Img = ({
  tone = "light",
  className = "",
}: {
  tone?: "light" | "mid" | "dark";
  className?: string;
}) => {
  const bg =
    tone === "dark"
      ? "bg-[#3a3a38]"
      : tone === "mid"
      ? "bg-[#b8b2a6]"
      : "bg-[#dddad5]";
  return (
    <div
      className={`${bg} w-full h-full flex items-center justify-center ${className}`}
    >
      <svg
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke={tone === "dark" ? "#888" : "#a09c97"}
        strokeWidth="0.8"
        className="opacity-60"
      >
        <rect x="3" y="3" width="18" height="18" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </div>
  );
};

// ─── NAVBAR ────────────────────────────────────────────────────────────────
const Navbar = () => {
  const [open, setOpen] = useState(false);
  const links = ["Services", "Projects", "About", "Process", "Contact"];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#e8e4de]">
      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-[18px] md:px-12 lg:px-20 h-[76px] md:h-[120px] flex items-center justify-between">
        {/* Logo */}
        <a href="#hero" className="flex items-center flex-shrink-0 min-w-0">
          <img
            src="/logo.png"
            alt="Del Valle Landscaping"
            className="h-[50px] md:h-[104px] w-auto object-contain block"
            style={{ maxWidth: '260px' }}
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              className="text-[10px] tracking-[0.22em] uppercase text-[#6b6560] hover:text-[#1A1A1A] transition-colors duration-200"
            >
              {l}
            </a>
          ))}
        </nav>

        {/* Desktop CTA */}
        <a
          href="tel:7073276586"
          className="hidden md:inline-flex bg-[#2F3E2F] text-white text-[9px] tracking-[0.18em] uppercase px-5 py-2.5 hover:bg-[#253225] transition-colors duration-200"
        >
          Call / Text 707-327-6586
        </a>

        {/* Hamburger — mobile only */}
        <button
          className="md:hidden flex-shrink-0 flex items-center justify-center w-11 h-11 -mr-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          <div className="w-6 flex flex-col gap-[5px]">
            <span className={`block h-[1.5px] bg-[#1A1A1A] transition-all duration-300 ${open ? "rotate-45 translate-y-[6.5px]" : ""}`} />
            <span className={`block h-[1.5px] bg-[#1A1A1A] transition-all duration-300 ${open ? "opacity-0" : ""}`} />
            <span className={`block h-[1.5px] bg-[#1A1A1A] transition-all duration-300 ${open ? "-rotate-45 -translate-y-[6.5px]" : ""}`} />
          </div>
        </button>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div className="md:hidden bg-white border-t border-[#e8e4de] px-[18px] pt-4 pb-6 flex flex-col">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className="text-[13px] tracking-[0.18em] uppercase text-[#3a3530] hover:text-[#1A1A1A] transition-colors py-[14px] border-b border-[#f0ece6] last:border-b-0"
            >
              {l}
            </a>
          ))}
          <a
            href="tel:7073276586"
            onClick={() => setOpen(false)}
            className="mt-5 text-[13px] tracking-[0.18em] uppercase text-white bg-[#2F3E2F] text-center py-4 hover:bg-[#253225] transition-colors"
          >
            Call / Text 707-327-6586
          </a>
        </div>
      )}
    </header>
  );
};

// ─── HERO ─────────────────────────────────────────────────────────────────
const Hero = () => (
  <section
    id="hero"
    className="relative pt-[76px] md:pt-[120px] min-h-[100svh] md:min-h-screen flex items-center overflow-hidden"
  >
    {/* Background image placeholder */}
    <div className="absolute inset-0">
      <Img tone="mid" className="absolute inset-0" />
    </div>

    {/* Soft light overlay — gradient from dark bottom to lighter top */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/65" />

    {/* Content — centered */}
    <div className="relative z-10 w-full py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20 flex flex-col items-center text-center">
        {/* Label */}
        <p className="text-[9px] sm:text-[10px] tracking-[0.32em] uppercase text-white/60 mb-6 md:mb-8 font-light">
          Sonoma County&nbsp;&bull;&nbsp;Outdoor Construction
        </p>

        {/* Headline */}
        <h1 className="font-serif text-[40px] sm:text-5xl md:text-7xl lg:text-[82px] font-medium leading-[1.08] text-white mb-5 md:mb-8 tracking-[-0.01em] max-w-4xl">
          Built with{" "}
          <em className="italic font-normal">Precision.</em>
          <br />
          Made to Last.
        </h1>

        {/* Divider */}
        <div className="w-10 h-px bg-white/30 mb-6 md:mb-8" />

        {/* Subtitle */}
        <p className="text-[15px] md:text-base text-white/75 leading-[1.9] max-w-xl mb-10 md:mb-14 font-light">
          Premium pavers, concrete, stonework, fences, decks, landscape
          installation, and structural outdoor work.
        </p>

        {/* Buttons */}
        <div className="flex flex-col gap-3 w-full max-w-xs sm:max-w-none sm:flex-row sm:gap-4 sm:justify-center">
          <a
            href="#contact"
            className="bg-[#2F3E2F] text-white text-[10px] tracking-[0.22em] uppercase px-9 py-4 hover:bg-[#253225] transition-colors duration-300 text-center"
          >
            Request a Free Estimate
          </a>
          <a
            href="tel:7073276586"
            className="border border-white/50 text-white text-[10px] tracking-[0.22em] uppercase px-9 py-4 hover:border-white hover:bg-white/10 transition-all duration-300 text-center"
          >
            Call / Text 707-327-6586
          </a>
        </div>
      </div>
    </div>

    {/* Scroll hint */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
      <div className="w-px h-10 bg-white animate-pulse" />
    </div>
  </section>
);

// ─── CATEGORY PANELS ──────────────────────────────────────────────────────
const categories = [
  {
    label: "Hardscaping",
    title: "Pavers, Concrete & Stonework",
    tone: "mid" as const,
    image: "/pavers-stonework.png",
    imgFilter: undefined,
    imgPosition: undefined,
    url: "/pavers-concrete-stonework",
  },
  {
    label: "Structures",
    title: "Fences & Deck Construction",
    tone: "dark" as const,
    image: "/fences-deck.png",
    imgFilter: "contrast(1.15) saturate(1.3) sepia(0.08) brightness(0.97)",
    imgPosition: "65% center",
    url: "/fences-deck-construction",
  },
  {
    label: "Outdoors",
    title: "Landscape Installation",
    tone: "mid" as const,
    image: "/landscape-installation.png",
    imgFilter: undefined,
    imgPosition: undefined,
    url: "/landscape-installation",
  },
  {
    label: "Foundation",
    title: "Structural & Ground Work",
    tone: "dark" as const,
    image: "/structural-ground-work.jpg",
    imgFilter: undefined,
    imgPosition: undefined,
    url: "/structural-ground-work",
  },
];

const ServicePanels = () => (
  <section id="services" className="bg-white">
    {/* 4-up on lg, 2x2 on sm, stacked on mobile */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      {categories.map((cat, i) => (
        <Link
          key={i}
          to={cat.url}
          className="relative h-[72vw] sm:h-[52vw] md:h-[50vw] lg:h-[520px] xl:h-[600px] cursor-pointer group overflow-hidden block"
        >
          {/* Image with zoom on hover */}
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105">
            {cat.image ? (
              <img
                src={cat.image}
                alt={cat.title}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  objectPosition: (cat as any).imgPosition ?? "center",
                  filter: (cat as any).imgFilter ?? "none",
                }}
              />
            ) : (
              <Img tone={cat.tone} className="absolute inset-0" />
            )}
          </div>

          {/* Base overlay: dark olive at 60% + gradient darker at bottom */}
          <div
            className="absolute inset-0 transition-opacity duration-500"
            style={{
              background: cat.image
                ? "linear-gradient(to top, rgba(47,62,47,0.90) 0%, rgba(47,62,47,0.65) 45%, rgba(47,62,47,0.45) 100%)"
                : "linear-gradient(to top, rgba(22,30,24,0.88) 0%, rgba(22,30,24,0.38) 50%, rgba(22,30,24,0.12) 100%)",
            }}
          />
          {/* Hover: overlay becomes slightly darker */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            style={{
              background: cat.image
                ? "linear-gradient(to top, rgba(20,30,20,0.96) 0%, rgba(20,30,20,0.72) 45%, rgba(20,30,20,0.55) 100%)"
                : "rgba(0,0,0,0.12)",
            }}
          />

          {/* Text block bottom-left with vertical accent line */}
          <div className="absolute bottom-0 left-0 p-7 md:p-9 z-10 flex items-end gap-4">
            {/* Vertical accent line — soft olive for real-photo card, white/40 for placeholders */}
            <div
              className="w-px self-stretch min-h-[56px] flex-shrink-0 transition-all duration-500"
              style={{
                backgroundColor: cat.image
                  ? "rgba(85,107,79,0.85)"
                  : "rgba(255,255,255,0.40)",
              }}
            />

            <div>
              {/* Small label uppercase */}
              <p
                className="text-[9px] tracking-[0.38em] uppercase mb-2.5 font-light transition-opacity duration-500"
                style={{ color: cat.image ? "rgba(255,255,255,0.60)" : "rgba(255,255,255,0.50)" }}
              >
                {cat.label}
              </p>
              {/* Main title — larger & slightly more spaced for real-photo card */}
              <h3
                className={`font-serif font-medium text-white leading-snug transition-all duration-500 ${
                  cat.image
                    ? "text-[24px] md:text-[26px] lg:text-[28px] tracking-[-0.01em] group-hover:opacity-100"
                    : "text-[22px] md:text-2xl lg:text-[26px] tracking-tight"
                }`}
              >
                {cat.title}
              </h3>
            </div>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

// ─── PROJECT COLLAGE ──────────────────────────────────────────────────────
const FeaturedProject = () => (
  <section id="projects" className="py-10 md:py-20 bg-white">
    <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-20">

      {/* Section header — left-aligned premium style */}
      <div className="mb-8 md:mb-12">
        <p className="text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#B8B2A6] mb-4 font-light"></p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-12">
          <h2 className="font-serif text-[30px] sm:text-4xl md:text-5xl lg:text-[58px] font-medium text-[#1A1A1A] leading-[1.08] tracking-[-0.01em] max-w-xl">
            Recent Outdoor
            <br />
            <em className="italic font-normal">Construction</em> Projects
          </h2>
          <p className="text-sm md:text-base text-[#6b6560] leading-[1.85] font-light max-w-sm md:mb-1 md:text-right">
            A closer look at the craftsmanship, structure, and finish
            behind every project.
          </p>
        </div>
        <div className="mt-8 w-full h-px bg-[#e8e4de]" />
      </div>

      {/* Collage layout */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-3">

        {/* Main large image — spans 8 cols on desktop */}
        <div className="md:col-span-8 overflow-hidden h-[70vw] sm:h-[56vw] md:h-full min-h-[320px] md:min-h-[580px] group cursor-pointer">
          <img
            src="/project-main.jpg"
            alt="Flagstone patio with mature oak tree — Sonoma County"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          />
        </div>

        {/* Right column — 3 stacked smaller images */}
        <div className="md:col-span-4 grid grid-cols-1 gap-3 md:gap-3">

          {/* Detail image — top */}
          <div className="overflow-hidden group cursor-pointer" style={{ height: "clamp(160px, 22vw, 186px)" }}>
            <img
              src="/project-detail.jpg"
              alt="Cobblestone paver detail — craftsmanship close-up"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>

          {/* Process / construction image — middle */}
          <div className="overflow-hidden group cursor-pointer" style={{ height: "clamp(160px, 22vw, 186px)" }}>
            <img
              src="/project-process.jpg"
              alt="Paver walkway in progress — construction phase"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>

          {/* Final result image — bottom */}
          <div className="overflow-hidden group cursor-pointer" style={{ height: "clamp(160px, 22vw, 186px)" }}>
            <img
              src="/project-result.jpg"
              alt="Drainage groundwork — structural foundation installation"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
            />
          </div>

        </div>

      </div>

      {/* Bottom caption bar */}
      <div className="mt-8 md:mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-[10px] text-[#B8B2A6] tracking-[0.30em] uppercase font-light">
          Selected Projects
        </p>
        <a
          href="#contact"
          className="text-[10px] tracking-[0.22em] uppercase text-[#2F3E2F] border-b border-[#2F3E2F]/30 pb-px hover:border-[#2F3E2F] transition-colors self-start sm:self-auto"
        >
          Request a Free Estimate →
        </a>
      </div>

    </div>
  </section>
);

// ─── WHY CHOOSE US ────────────────────────────────────────────────────────
const reasons = [
  {
    number: "01",
    title: "20+ Years of Experience",
    desc: "Two decades of hands-on expertise in hardscaping, structural construction, and landscape installation throughout Sonoma County.",
  },
  {
    number: "02",
    title: "Premium Materials Only",
    desc: "We source the highest-grade natural stone, concrete, lumber, and structural components — built to last a lifetime.",
  },
  {
    number: "03",
    title: "Structural Precision",
    desc: "Every project is engineered for drainage, stability, and long-term performance. No shortcuts, ever.",
  },
  {
    number: "04",
    title: "Owner on Every Job",
    desc: "Lucio Valle personally leads each project from the first consultation through the final walkthrough.",
  },
];

const WhyUs = () => (
  <section className="py-10 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-[24px] md:px-12 lg:px-20">
      <div className="text-center mb-8 md:mb-16">
        <p className="text-[11px] md:text-[10px] tracking-[0.28em] md:tracking-[0.32em] text-[#B8B2A6] uppercase mb-4">
          Our Commitment
        </p>
        <h2 className="font-serif text-[34px] md:text-6xl lg:text-7xl font-medium text-[#1A1A1A] tracking-[-0.01em]">
          Why Choose Del Valle Landscaping
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
        {reasons.map((r, i) => (
          <div key={i} className="flex flex-col">
            <span className="font-serif text-6xl font-light text-[#ebe7e1] mb-5 leading-none">
              {r.number}
            </span>
            <div className="w-8 h-px bg-[#B8B2A6] mb-6" />
            <h3 className="text-[13px] md:text-[10px] font-medium tracking-[0.18em] md:tracking-[0.2em] uppercase text-[#1A1A1A] mb-3">
              {r.title}
            </h3>
            <p className="text-base md:text-sm text-[#6b6560] leading-[1.8] font-light">
              {r.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── ABOUT ────────────────────────────────────────────────────────────────
const About = () => (
  <section id="about" className="py-10 md:py-24 bg-[#F4F1EA]">
    <div className="max-w-7xl mx-auto px-[24px] md:px-12 lg:px-20">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[11px] md:text-[10px] tracking-[0.28em] md:tracking-[0.32em] text-[#B8B2A6] uppercase mb-4">
          Our Story
        </p>
        <h2 className="font-serif text-[34px] md:text-6xl lg:text-7xl font-medium text-[#1A1A1A] mb-8 md:mb-10 tracking-[-0.01em]">
          About Us
        </h2>
        <div className="w-10 h-px bg-[#B8B2A6] mx-auto mb-6 md:mb-10" />
        <p className="text-base text-[#6b6560] leading-[1.8] font-light mb-6">Del Valle Landscaping was founded by Lucio Valle with a singular purpose: to bring architectural craftsmanship to outdoor spaces in California CA. Over two decades, we have built a reputation for structural quality, material integrity, and work that stands the test of time.</p>
        <p className="text-base text-[#6b6560] leading-[1.8] font-light">
          Every project — whether a stone patio, retaining wall, or full
          landscape installation — is treated as a permanent addition to your
          property. We build it right, once.
        </p>
      </div>
    </div>
  </section>
);

// ─── PROCESS ─────────────────────────────────────────────────────────────
const steps = [
  {
    num: "01",
    title: "Consultation",
    desc: "We meet on-site to assess your property, understand your vision, and evaluate structural and drainage requirements.",
  },
  {
    num: "02",
    title: "Planning",
    desc: "We develop a clear project plan: materials, scope, timeline, and cost — all agreed upon before a single shovel hits the ground.",
  },
  {
    num: "03",
    title: "Construction",
    desc: "Our team executes with precision — foundation work, structural build, material installation, and finishing details.",
  },
  {
    num: "04",
    title: "Final Result",
    desc: "We walk the site with you to ensure every element meets our standard of excellence before we consider the job complete.",
  },
];

const Process = () => (
  <section id="process" className="py-10 md:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-[24px] md:px-12 lg:px-20">
      <div className="text-center mb-8 md:mb-16">
        <p className="text-[11px] md:text-[10px] tracking-[0.28em] md:tracking-[0.32em] text-[#B8B2A6] uppercase mb-4">
          How We Work
        </p>
        <h2 className="font-serif text-[34px] md:text-6xl lg:text-7xl font-medium text-[#1A1A1A] tracking-[-0.01em]">
          Our Process
        </h2>
      </div>

      <div className="max-w-xl mx-auto">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[23px] top-0 bottom-0 w-px bg-[#e4e0da]" />

          <div className="flex flex-col">
            {steps.map((s, i) => (
              <div key={i} className="relative flex gap-8 md:gap-10 pb-8 md:pb-12 last:pb-0">
                {/* Step circle */}
                <div className="relative z-10 flex-shrink-0 w-12 h-12 bg-white border border-[#e4e0da] flex items-center justify-center">
                  <span className="font-serif text-xs font-medium text-[#B8B2A6]">
                    {s.num}
                  </span>
                </div>
                <div className="pt-3 min-w-0">
                  <h3 className="text-[13px] md:text-[10px] font-medium tracking-[0.18em] md:tracking-[0.22em] uppercase text-[#1A1A1A] mb-3">
                    {s.title}
                  </h3>
                  <p className="text-base md:text-sm text-[#6b6560] leading-[1.8] font-light">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── CALL TO ACTION / CONTACT ─────────────────────────────────────────────
const Contact = () => (
  <section id="contact" className="py-10 md:py-24 bg-[#1A1A1A]">
    <div className="max-w-7xl mx-auto px-[24px] md:px-12 lg:px-20">
      <div className="max-w-2xl mx-auto text-center">
        <p className="text-[11px] md:text-[10px] tracking-[0.28em] md:tracking-[0.32em] text-[#B8B2A6] uppercase mb-6">
          Start Your Project
        </p>
        <h2 className="font-serif text-[34px] md:text-6xl lg:text-7xl font-medium text-white mb-6 leading-[1.1] md:leading-[1.04] tracking-[-0.01em]">
          Request a
          <br />
          <em className="italic font-normal">Free Estimate</em>
        </h2>
        <p className="text-base text-[#9a958f] font-light leading-[1.8] mb-8 md:mb-16">
          Call or text directly to get a fast quote for your project.
          We respond the same day.
        </p>

        {/* Action buttons — stacked full-width on mobile */}
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 sm:justify-center mb-10 md:mb-16">
          <a
            href="tel:7073276586"
            className="bg-white text-[#1A1A1A] text-[11px] md:text-[10px] tracking-[0.2em] md:tracking-[0.22em] uppercase px-10 py-4 md:py-3.5 hover:bg-[#f0ece4] transition-colors duration-300 text-center w-full sm:w-auto"
          >
            Call Now
          </a>
          <a
            href="sms:7073276586"
            className="border border-[#3a3a38] text-white text-[11px] md:text-[10px] tracking-[0.2em] md:tracking-[0.22em] uppercase px-10 py-4 md:py-3.5 hover:border-[#666] transition-colors duration-300 text-center w-full sm:w-auto"
          >
            Send a Text Message
          </a>
        </div>

        {/* Info */}
        <div className="border-t border-[#2a2a28] pt-6 md:pt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-8 text-center">
          <div>
            <p className="text-[11px] md:text-[9px] tracking-[0.22em] md:tracking-[0.25em] uppercase text-[#6b6560] mb-2">Owner</p>
            <p className="text-base md:text-sm text-[#c8c4be] font-light">Lucio Valle</p>
          </div>
          <div>
            <p className="text-[11px] md:text-[9px] tracking-[0.22em] md:tracking-[0.25em] uppercase text-[#6b6560] mb-2">Phone</p>
            <a
              href="tel:7073276586"
              className="text-base md:text-sm text-[#c8c4be] font-light hover:text-white transition-colors"
            >
              707-327-6586
            </a>
          </div>
          <div>
            <p className="text-[11px] md:text-[9px] tracking-[0.22em] md:tracking-[0.25em] uppercase text-[#6b6560] mb-2">Service Area</p>
            <p className="text-base md:text-sm text-[#c8c4be] font-light">Sonoma County, CA</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── FOOTER ───────────────────────────────────────────────────────────────
const Footer = () => (
  <footer className="py-8 md:py-10 bg-white border-t border-[#e8e4de]">
    <div className="max-w-7xl mx-auto px-[24px] md:px-12 lg:px-20 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
      <div className="flex items-center gap-3">
        <img
          src="/logo.png"
          alt="Del Valle Landscaping"
          className="h-8 md:h-9 w-auto object-contain"
        />
      </div>
      <p className="text-[10px] text-[#B8B2A6] tracking-widest">
        © {new Date().getFullYear()} Del Valle Landscaping · Sonoma County, CA · Lic. #07646
      </p>
      <a
        href="tel:7073276586"
        className="text-[11px] md:text-[10px] text-[#6b6560] tracking-widest hover:text-[#1A1A1A] transition-colors"
      >
        707-327-6586
      </a>
    </div>
  </footer>
);

// ─── APP ROOT ────────────────────────────────────────────────────────────
function App() {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Navbar />
      <Hero />
      <ServicePanels />
      <FeaturedProject />
      <WhyUs />
      <About />
      <Process />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
